import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus } from "lucide-react";

const NONE = "__none__";

/**
 * Add/Edit dialog for Client or Partner entities.
 *
 * Props:
 *   open, onOpenChange — Dialog visibility
 *   editing — boolean, true when editing existing entity
 *   form, setForm — controlled form state
 *   managers — list of {id, name, role} for the AM dropdown
 *   typeLabel — "Client" | "Partner"
 *   onOpenNew — invoked when the trigger button is clicked
 *   onSave — invoked when Save button is clicked
 */
export default function EntityFormDialog({
  open,
  onOpenChange,
  editing,
  form,
  setForm,
  managers,
  typeLabel,
  onOpenNew,
  onSave,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button
          onClick={onOpenNew}
          className="rounded-sm bg-[color:var(--aisensy-dark)] hover:bg-[color:var(--aisensy-dark-2)] text-white"
          data-testid="add-entity-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Add {typeLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">
            {editing ? `Edit ${typeLabel}` : `Add new ${typeLabel}`}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <Label className="eyebrow">
              Name <span className="text-[color:var(--overdue)]">*</span>
            </Label>
            <Input
              className="rounded-sm mt-1"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="entity-name-input"
              required
            />
          </div>
          <div>
            <Label className="eyebrow">
              Email <span className="text-[color:var(--overdue)]">*</span>
            </Label>
            <Input
              type="email"
              className="rounded-sm mt-1"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="entity-email-input"
              required
            />
          </div>
          <div>
            <Label className="eyebrow">
              Phone <span className="text-[color:var(--overdue)]">*</span>
            </Label>
            <Input
              className="rounded-sm mt-1"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="entity-phone-input"
              required
            />
          </div>
          <div>
            <Label className="eyebrow">GSTIN</Label>
            <Input
              className="rounded-sm mt-1"
              value={form.gstin}
              onChange={(e) => setForm({ ...form, gstin: e.target.value })}
              data-testid="entity-gstin-input"
            />
          </div>
          <div>
            <Label className="eyebrow">Address</Label>
            <Input
              className="rounded-sm mt-1"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              data-testid="entity-address-input"
            />
          </div>
          <div className="sm:col-span-2">
            <Label className="eyebrow">
              Account Manager <span className="text-ink-2 normal-case font-normal ml-1">(optional)</span>
            </Label>
            <Select
              value={form.account_manager_id}
              onValueChange={(v) => setForm({ ...form, account_manager_id: v })}
            >
              <SelectTrigger className="rounded-sm mt-1" data-testid="entity-account-manager">
                <SelectValue placeholder="Select account manager" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>No account manager</SelectItem>
                {managers.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name} · {m.role.replace("_", " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label className="eyebrow">
              Payment Timeline (days) <span className="text-[color:var(--overdue)]">*</span>
            </Label>
            <Select
              value={form.payment_timeline_days}
              onValueChange={(v) => setForm({ ...form, payment_timeline_days: v })}
            >
              <SelectTrigger className="rounded-sm mt-1" data-testid="entity-payment-timeline">
                <SelectValue placeholder="Days after invoice date" />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                {Array.from({ length: 60 }, (_, i) => i + 1).map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n} {n === 1 ? "day" : "days"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-[10px] text-ink-2 mt-1">
              Invoices for this {typeLabel.toLowerCase()} will auto-set the due date to invoice date + this many days.
            </div>
          </div>
          <div className="sm:col-span-2">
            <Label className="eyebrow">Notes</Label>
            <Textarea
              className="rounded-sm mt-1"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-sm">
            Cancel
          </Button>
          <Button
            onClick={onSave}
            className="rounded-sm bg-[color:var(--aisensy-dark)] hover:bg-[color:var(--aisensy-dark-2)] text-white"
            data-testid="entity-save-btn"
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
